import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Search as SearchIcon } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../lib/api';
import { slugify } from './SuburbLanding';

export default function PerthTradesIndex() {
  const [combos, setCombos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Every Perth trade & suburb combination — Real Tradies directory index';
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Complete directory of verified Perth tradies by trade and suburb. Plumbers, electricians, carpenters and more, all serving Fremantle, Subiaco, Scarborough, Joondalup and every Perth suburb.');
    Promise.all([api.seoSuburbIndex(), api.categories()]).then(([a, b]) => {
      setCombos(a.data);
      setCategories(b.data);
      setLoading(false);
    });
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const c of combos) {
      if (q && !`${c.category} ${c.suburb}`.toLowerCase().includes(q.toLowerCase())) continue;
      if (!g[c.category]) g[c.category] = [];
      g[c.category].push(c);
    }
    return g;
  }, [combos, q]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || id;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="perth-trades-index">
      <Header />
      <div className="mx-auto max-w-6xl px-6 py-12 md:px-10">
        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D96C4E]">Perth · WA directory</div>
        <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-6xl">
          Every trade × every Perth suburb
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-[#5C5B5A]">
          Browse the entire Real Tradies index. Click any combination for hand-verified tradies operating in that Perth
          suburb.
        </p>

        <div className="mt-8 flex items-center gap-2 border border-[#1C1B1A] bg-white px-3 py-2" data-testid="index-search">
          <SearchIcon size={16} className="text-[#8A8988]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter — e.g. plumber fremantle"
            data-testid="index-search-input"
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>

        {loading ? (
          <div className="mt-10 text-sm text-[#8A8988]">Building directory...</div>
        ) : (
          <div className="mt-10 space-y-10" data-testid="index-groups">
            {Object.entries(grouped).map(([cat, list]) => (
              <div key={cat} data-testid={`index-group-${cat}`}>
                <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#1A362D]">
                  <MapPin size={12} /> {catName(cat)}s
                </div>
                <div className="grid grid-cols-2 gap-px border border-[#E5E4E0] bg-[#E5E4E0] sm:grid-cols-3 md:grid-cols-4">
                  {list.map((c) => (
                    <Link
                      key={`${c.category}-${c.suburb}`}
                      to={`/l/${c.category}/${slugify(c.suburb)}`}
                      data-testid={`index-tile-${c.category}-${slugify(c.suburb)}`}
                      className="flex items-center justify-between bg-white p-4 transition-colors hover:bg-[#F3F2EE]"
                    >
                      <div>
                        <div className="text-sm font-semibold text-[#1C1B1A]">{c.suburb}</div>
                        <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">
                          {c.count} tradie{c.count !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-[#1A362D]" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            {Object.keys(grouped).length === 0 && (
              <div className="border border-dashed border-[#E5E4E0] bg-white p-8 text-sm text-[#8A8988]">
                No matches. Try a different search.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
